import { useState, useCallback } from 'react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import {
    useGetAllDealerOtherLaborPriceQuery,
    useCreateDealerOtherLaborPriceMutation,
    useUpdateDealerOtherLaborPriceMutation,
    useDeleteDealerOtherLaborPriceMutation
} from '../../../../api';
import { useSelector } from 'react-redux';
import { selectUserParams } from '../../../../store/slices/userParamsSlice';
import toastService from '../../../../utils/toastService';

const useOtherLaborPriceData = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const userParams = useSelector(selectUserParams);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Get current language ID
    const languageId = getCurrentLanguageId();

    // Get list
    const { data, isLoading, refetch } = useGetAllDealerOtherLaborPriceQuery({
        pageNumber: page,
        pageSize,
        languageId
    });

    const [createItem, { isLoading: isCreating }] = useCreateDealerOtherLaborPriceMutation();
    const [updateItem, { isLoading: isUpdating }] = useUpdateDealerOtherLaborPriceMutation();
    const [deleteItem, { isLoading: isDeleting }] = useDeleteDealerOtherLaborPriceMutation();

    const items = data?.items || [];
    const totalRecords = data?.totalCount || 0;

    const handleNew = useCallback(() => {
        setSelectedItem(null);
        setDialogVisible(true);
    }, []);

    const handleEdit = useCallback((rowData) => {
        setSelectedItem(rowData);
        setDialogVisible(true);
    }, []);

    const handleSave = useCallback(
        async (formData) => {
            try {
                const payload = {
                    Price: formData.price,
                    Currency: formData.currency,
                    ContractStartDate: formData.contractStartDate 
                        ? (formData.contractStartDate instanceof Date 
                            ? formData.contractStartDate.toISOString() 
                            : formData.contractStartDate)
                        : null,
                    ContractEndDate: formData.contractEndDate 
                        ? (formData.contractEndDate instanceof Date 
                            ? formData.contractEndDate.toISOString() 
                            : formData.contractEndDate)
                        : null,
                    BosBranchCode: userParams.dealerId?.toString() || null,
                    BosBranchSiteCode: userParams.siteUseId?.toString() || null,
                    IsActive: formData.id ? (formData.isActive !== undefined ? formData.isActive : 1) : 1 // For update: use formData.isActive, for create: always 1
                };

                if (formData.id) {
                    payload.Id = formData.id;
                    await updateItem(payload).unwrap();
                    toastService.success(
                        th('otherLaborPrice.toast.updateSuccess') || 'İşçilik ücreti başarıyla güncellendi'
                    );
                } else {
                    // Add creation specific fields for new records
                    payload.CreatedBy = userParams.userId || 1;
                    payload.CreatedName = userParams.dealerFullName || 'System';
                    payload.CreatedDate = new Date().toISOString();
                    payload.CreatedSession = 'web';

                    await createItem(payload).unwrap();
                    toastService.success(
                        th('otherLaborPrice.toast.createSuccess') || 'İşçilik ücreti başarıyla oluşturuldu'
                    );
                }
                await refetch();
                setDialogVisible(false);
                setSelectedItem(null);
            } catch (error) {
                console.error('Save error:', error);
                // Backend'den gelen hata mesajını göster
                const errorMessage = error?.data?.message || error?.data?.errorMessage || error?.message || 
                    th('otherLaborPrice.toast.saveError') || 'Kayıt sırasında bir hata oluştu';
                toastService.error(errorMessage);
                throw error;
            }
        },
        [createItem, updateItem, refetch, th, userParams]
    );

    const handleDelete = useCallback(
        async (id) => {
            try {
                await deleteItem({ id }).unwrap();
                toastService.success(
                    th('otherLaborPrice.toast.deleteSuccess') || 'İşçilik ücreti başarıyla silindi'
                );
                await refetch();
            } catch (error) {
                console.error('Delete error:', error);
                toastService.error(
                    th('otherLaborPrice.toast.deleteError') || 'Silme işlemi sırasında bir hata oluştu'
                );
                throw error;
            }
        },
        [deleteItem, refetch, th]
    );

    const handlePage = useCallback((event) => {
        setPage(event.page + 1);
        setPageSize(event.rows);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setDialogVisible(false);
        setSelectedItem(null);
    }, []);

    return {
        // Data
        items,
        totalRecords,
        isLoading,
        isCreating,
        isUpdating,
        isDeleting,

        // Dialog state
        dialogVisible,
        selectedItem,

        // Pagination
        page,
        pageSize,

        // Handlers
        handleNew,
        handleEdit,
        handleSave,
        handleDelete,
        handlePage,
        handleCloseDialog,
        refetch
    };
};

export default useOtherLaborPriceData;

